import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { actions as userActions } from '../../actions/userActions';
import { actions as locationActions } from '../../actions/locationActions';
import * as types from '../../constants/actionTypes';
import Drawer from '../Common/Drawer/Drawer';
import UserProfileForm from './UserProfileForm';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import Notification from '../Common/Notification';
import { USER_LOCATION_ACCESS_OPTIONS } from '../../constants/constants';
import { isValidEmailAddress } from '../../helpers/validation';

export class UserProfileDrawer extends Component {
    constructor(props, context) {
        super(props, context);

        // Allow initial user prop to override defaults (for testability and correct initial state)
        const initialUser = (props.user && typeof props.user === 'object') ? props.user : {};
        // Determine initial selectedUserLocationAccess
        let initialSelectedUserLocationAccess;
        if (typeof initialUser.selectedUserLocationAccess !== 'undefined') {
            initialSelectedUserLocationAccess = initialUser.selectedUserLocationAccess;
        } else if (typeof initialUser.grantAccessToAllLocations !== 'undefined') {
            if (initialUser.grantAccessToAllLocations === false) {
                initialSelectedUserLocationAccess = USER_LOCATION_ACCESS_OPTIONS.NO.value;
            } else if (initialUser.grantAccessToAllLocations === true) {
                initialSelectedUserLocationAccess = USER_LOCATION_ACCESS_OPTIONS.YES.value;
            } else {
                initialSelectedUserLocationAccess = USER_LOCATION_ACCESS_OPTIONS.RULES.value;
            }
        } else {
            initialSelectedUserLocationAccess = USER_LOCATION_ACCESS_OPTIONS.NO.value;
        }
        const newUser = {
            id: 0,
            subscriptionId: 0,
            firstName: '',
            lastName: '',
            userName: '',
            email: '',
            workPhone: '',
            jobTitle: '',
            roleId: '',
            countryId: '',
            managementCompanyId: '',
            communityIds: '',
            departmentIds: [],
            excludeAsProjectCoach: false,
            allowTaskListExportToExcel: false,
            allowLocationTaskDateChanges: false,
            allowLocationTaskBudgetEntry: false,
            lastLoginDate: '',
            isActive: true,
            selectedUserLocationAccess: initialSelectedUserLocationAccess,
            internalComment: '',
            userLocationRules: {
                areas: '',
                brands: '',
                continents: '',
                countries: '',
                locationTypes: '',
                managementCompanies: '',
                regions: '',
                ...(initialUser.userLocationRules || {})
            },
            subscriptionNames: '',
            ...initialUser
        };

        this.state = {
            isFormValid: true,
            hasEmailBeenModified: false,
            isSubscriptionProjectEnabled: false,
            isSubscriptionSsoEnabled: false,
            isSubscriptionTaskFinancialsActive: false,
            user: newUser,
            countries: [],
            roles: [],
            managementCompanies: [],
            communities: [],
            departments: [],
            isLoading: false,
            isUserLocationAccessFormValid: false,
        };
    }

    async componentDidMount() {
        try {
            this.setState({ isLoading: true });
            await Promise.all([this.loadFilters(), this.loadUser()]);
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            this.setState({ isLoading: false });
        }
    }

    componentDidUpdate = (prevProps) => {
        if (this.props.userId && this.props.userId !== prevProps.userId) {
            this.loadUser();
        }
    };

    onFormChange = (name, value) => {
        this.setState({ user: { ...this.state.user, [name]: value } });
    };

    onSSOToggleChange = (value) => {
        this.setState({
            user: {
                ...this.state.user,
                isSSOUser: value,
            },
        });
    };

    onUserLocationAccessFormChange = (name, value) => {
        const user = {
            ...this.state.user,
            userLocationRules: {
                ...this.state.user.userLocationRules,
                [name]: value,
            },
        };
        const isUserLocationAccessFormValid = this.validateUserLocationAccessRules(user);
        this.setState({
            user: user,
            isUserLocationAccessFormValid: isUserLocationAccessFormValid,
        });
    };

    onCancel = () => {
        this.setState({ isFormValid: true, drawerOpen: false });
        this.props.onCancel();
    };

    parseGrantAccessToAllLocations = (grantAccessToAllLocations) => {
        if (grantAccessToAllLocations === false) {
            return USER_LOCATION_ACCESS_OPTIONS.NO.value;
        } else if (grantAccessToAllLocations === true) {
            return USER_LOCATION_ACCESS_OPTIONS.YES.value;
        } else {
            return USER_LOCATION_ACCESS_OPTIONS.RULES.value;
        }
    };

    getUserLocationRuleValues = (user) => {
        const response = {
            areas: '',
            brands: '',
            continents: '',
            countries: '',
            locationTypes: '',
            managementCompanies: '',
            regions: '',
        };

        if (user.areaIds) {
            response.areas = user.areaIds.join(',');
        }
        if (user.brandIds) {
            response.brands = user.brandIds.join(',');
        }
        if (user.continentIds) {
            response.continents = user.continentIds.join(',');
        }
        if (user.countryIds) {
            response.countries = user.countryIds.join(',');
        }
        if (user.locationTypeIds) {
            response.locationTypes = user.locationTypeIds.join(',');
        }
        if (user.managementCompanyIds) {
            response.managementCompanies = user.managementCompanyIds.join(',');
        }
        if (user.regionIds) {
            response.regions = user.regionIds.join(',');
        }

        return response;
    };

    loadUser = async () => {
        try {
            const response = await this.props.userActions.loadUser(this.props.subscriptionId, this.props.userId);
            if (response.type === types.LOAD_USER_SUCCESS) {
                const apiUser = response.payload.user;
                // Use selectedUserLocationAccess from apiUser if present, otherwise parse from grantAccessToAllLocations
                const selectedUserLocationAccess =
                    typeof apiUser.selectedUserLocationAccess !== 'undefined'
                        ? apiUser.selectedUserLocationAccess
                        : this.parseGrantAccessToAllLocations(apiUser.grantAccessToAllLocations);
                this.setState({
                    user: {
                        ...this.state.user,
                        ...apiUser,
                        selectedUserLocationAccess,
                        userLocationRules: {
                            ...this.state.user.userLocationRules,
                            ...this.getUserLocationRuleValues(apiUser),
                        },
                    },
                });
            } else if (response.type === types.LOAD_USER_FAILURE) {
                Notification('error', 'An Error Occurred', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    loadFilters = async () => {
        try {
            const response = await this.props.userActions.loadInviteUserFilters(this.props.subscriptionId);
            if (response.type === types.LOAD_INVITE_USER_FILTERS_SUCCESS) {
                this.setState({
                    isSubscriptionProjectEnabled: response.payload.isSubscriptionProjectEnabled,
                    isSubscriptionSsoEnabled: response.payload.isSubscriptionSsoEnabled,
                    isSubscriptionTaskFinancialsActive: response.payload.isSubscriptionTaskFinancialsActive,
                    communities: response.payload.communities,
                    roles: response.payload.roles,
                    managementCompanies: response.payload.managementCompanies,
                    departments: response.payload.departments,
                    countries: response.payload.countries,
                });
            } else if (response.type === types.LOAD_INVITE_USER_FILTERS_FAILURE) {
                Notification('error', 'An Error Occurred', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    validateUserLocationAccessRules = (user) => {
        switch (user.selectedUserLocationAccess) {
            case USER_LOCATION_ACCESS_OPTIONS.YES.value:
            case USER_LOCATION_ACCESS_OPTIONS.NO.value: {
                return true;
            }
            case USER_LOCATION_ACCESS_OPTIONS.RULES.value: {
                const { areas, brands, continents, countries, locationTypes, managementCompanies, regions } = user.userLocationRules;

                // Consider the form valid is at least one of the multi-selects has at least one value,
                // or rather, consider the form invalid if NONE of them have ANY values
                if (
                    areas.trim().length <= 0 &&
                    brands.trim().length <= 0 &&
                    continents.trim().length <= 0 &&
                    countries.trim().length <= 0 &&
                    locationTypes.trim().length <= 0 &&
                    managementCompanies.trim().length <= 0 &&
                    regions.trim().length <= 0
                ) {
                    return false;
                }
                return true;
            }
            default: {
                return false;
            }
        }
    };

    validateForm = (user) => {
        const { firstName, lastName, email, userName, countryId, roleId, managementCompanyId, communityIds } = user;

        if (!(email && email.trim().length > 0 && isValidEmailAddress(email))) {
            Notification('error', 'Email address is not valid', '');
            return false;
        }

        const hasValidUserLocationAccessRules = this.validateUserLocationAccessRules(user);

        return (
            firstName &&
            firstName.trim().length > 0 &&
            lastName &&
            lastName.trim().length > 0 &&
            userName &&
            userName.trim().length > 0 &&
            roleId &&
            countryId &&
            managementCompanyId &&
            communityIds &&
            communityIds.length > 0 &&
            hasValidUserLocationAccessRules
        );
    };

    parseUserLocationRules = (rules) => {
        if (this.state.user.userLocationRules.areas.trim().length > 0) {
            rules.areaIds = this.state.user.userLocationRules.areas.split(',');
        }

        if (this.state.user.userLocationRules.brands.trim().length > 0) {
            rules.brandIds = this.state.user.userLocationRules.brands.split(',');
        }

        if (this.state.user.userLocationRules.continents.trim().length > 0) {
            rules.continentIds = this.state.user.userLocationRules.continents.split(',');
        }

        if (this.state.user.userLocationRules.countries.trim().length > 0) {
            rules.countryIds = this.state.user.userLocationRules.countries.split(',');
        }

        if (this.state.user.userLocationRules.locationTypes.trim().length > 0) {
            rules.locationTypeIds = this.state.user.userLocationRules.locationTypes.split(',');
        }

        if (this.state.user.userLocationRules.managementCompanies.trim().length > 0) {
            rules.managementCompanyIds = this.state.user.userLocationRules.managementCompanies.split(',');
        }

        if (this.state.user.userLocationRules.regions.trim().length > 0) {
            rules.regionIds = this.state.user.userLocationRules.regions.split(',');
        }

        if (this.state.user.userLocationRules.areas.trim().length > 0) {
            rules.areaIds = this.state.user.userLocationRules.areas.split(',');
        }

        return rules;
    };

    onSave = async () => {
        try {
            let user = this.state.user;
            let userLocationRules = {
                areaIds: [],
                brandIds: [],
                continentIds: [],
                countryIds: [],
                locationTypeIds: [],
                managementCompanyIds: [],
                regionIds: [],
            };
            let grantAccessToAllLocations = false; // Default to NO
            if (this.state.user.selectedUserLocationAccess === USER_LOCATION_ACCESS_OPTIONS.RULES.value) {
                userLocationRules = this.parseUserLocationRules(userLocationRules);
                grantAccessToAllLocations = null;
            } else if (this.state.user.selectedUserLocationAccess === USER_LOCATION_ACCESS_OPTIONS.YES.value) {
                grantAccessToAllLocations = true;
            }

            user = {
                ...user,
                grantAccessToAllLocations: grantAccessToAllLocations,
                ...userLocationRules,
            };

            if (this.validateForm(user)) {
                this.setState({ isFormValid: true, isLoading: true });

                const result = await this.props.userActions.updateUser(user);
                if (result.type === types.UPDATE_USER_SUCCESS) {
                    Notification('success', 'Success', 'Profile saved successfully');
                    this.setState({ isFormValid: true, drawerOpen: false });
                    this.props.onComplete();
                } else if (result.type === types.UPDATE_USER_FAILURE) {
                    Notification('error', 'Error', result.payload.message);
                    this.setState({ isLoading: false });
                }
            } else {
                this.setState({ isFormValid: false, isLoading: false });
            }
        } catch (error) {
            Notification('error', 'Error', error);
            this.setState({ isLoading: false });
        } finally {
            // Do not set isLoading state here. If the save was successful, the
            // component is unloaded already because of the redirect
        }
    };

    render() {
        return (
            <Drawer
                isWide={true}
                title='Edit User Profile'
                onSaveClick={this.onSave}
                saveText='Save Profile'
                onCancelClick={this.onCancel}
                menuButtons={this.props.menuButtons}>
                <LoadingIndicator isLoading={this.state.isLoading}>
                    <UserProfileForm
                        subscriptionId={this.props.subscriptionId}
                        user={this.state.user}
                        isFormValid={this.state.isFormValid}
                        isSubscriptionProjectEnabled={this.state.isSubscriptionProjectEnabled}
                        isSubscriptionSsoEnabled={this.state.isSubscriptionSsoEnabled}
                        isSubscriptionTaskFinancialsActive={this.state.isSubscriptionTaskFinancialsActive}
                        countries={this.state.countries}
                        roles={this.state.roles}
                        managementCompanies={this.state.managementCompanies}
                        communities={this.state.communities}
                        departments={this.state.departments}
                        onFormChange={this.onFormChange}
                        onSSOToggleChange={this.onSSOToggleChange}
                        onUserLocationAccessFormChange={this.onUserLocationAccessFormChange}
                        locationActions={this.props.locationActions}
                        isUserLocationAccessFormValid={this.state.isUserLocationAccessFormValid}
                    />
                </LoadingIndicator>
            </Drawer>
        );
    }
}

UserProfileDrawer.propTypes = {
    subscriptionId: PropTypes.string.isRequired,
    user: PropTypes.object,
    userId: PropTypes.string.isRequired,
    onComplete: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    userActions: PropTypes.object.isRequired,
    menuButtons: PropTypes.array.isRequired,
    locationActions: PropTypes.object.isRequired,
};

function mapDispatchToProps(dispatch) {
    return {
        userActions: bindActionCreators(userActions, dispatch),
        locationActions: bindActionCreators(locationActions, dispatch),
    };
}

export default connect(null, mapDispatchToProps)(UserProfileDrawer);
